import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import Modal from '../../components/UI/Modal';
import { partnersAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { Plus, Edit, Trash2, Image as ImageIcon, Save, X, ExternalLink } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const PartnerCard = styled(Card)`
  padding: 1.5rem;
  text-align: center;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const PartnerLogo = styled.div`
  width: 150px;
  height: 150px;
  margin: 0 auto 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const PartnerName = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const PartnerDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.$active ? props.theme.colors.success + '20' : props.theme.colors.error + '20'};
  color: ${props => props.$active ? props.theme.colors.success : props.theme.colors.error};
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${props => props.rows ? props.rows * 1.5 : 4}rem;
  padding: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary}05;
  }
`;

const PreviewImage = styled.div`
  margin-top: 1rem;
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const PartnersManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: null,
    website_url: '',
    order: 0,
    is_active: true
  });
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await partnersAPI.getAll({ all: true });
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name || '',
        description: partner.description || '',
        logo: null,
        website_url: partner.website_url || '',
        order: partner.order || 0,
        is_active: partner.is_active !== undefined ? partner.is_active : true
      });
      setLogoPreview(partner.logo_url ? getStorageUrl(partner.logo_url) : null);
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        description: '',
        logo: null,
        website_url: '',
        order: 0,
        is_active: true
      });
      setLogoPreview(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPartner(null);
    setFormData({
      name: '',
      description: '',
      logo: null,
      website_url: '',
      order: 0,
      is_active: true
    });
    setLogoPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You are not logged in. Please log in and try again.');
        window.location.href = '/login';
        return;
      }

      console.log('Submitting partner data:', formData);
      if (editingPartner) {
        await partnersAPI.update(editingPartner.id, formData);
      } else {
        await partnersAPI.create(formData);
      }
      handleCloseModal();
      fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        window.location.href = '/login';
      } else {
        alert('Failed to save partner. Please try again.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await partnersAPI.delete(id);
        fetchPartners();
      } catch (error) {
        console.error('Error deleting partner:', error);
        alert('Failed to delete partner. Please try again.');
      }
    }
  };

  return (
    <Container>
      <Header>
        <Title>Partners Management</Title>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <Plus size={20} style={{ marginRight: '0.5rem' }} />
          Add Partner
        </Button>
      </Header>

      {loading ? (
        <p>Loading partners...</p>
      ) : partners.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <p>No partners yet. Click "Add Partner" to create one.</p>
        </Card>
      ) : (
        <Grid>
          {partners.map((partner) => (
            <PartnerCard key={partner.id}>
              <StatusBadge $active={partner.is_active}>
                {partner.is_active ? 'Active' : 'Inactive'}
              </StatusBadge>
              <PartnerLogo>
                {partner.logo_url ? (
                  <img src={getStorageUrl(partner.logo_url)} alt={partner.name} />
                ) : (
                  <ImageIcon size={48} style={{ opacity: 0.3 }} />
                )}
              </PartnerLogo>
              <PartnerName>{partner.name}</PartnerName>
              {partner.description && (
                <PartnerDescription>{partner.description}</PartnerDescription>
              )}
              {partner.website_url && (
                <a
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.875rem',
                    color: '#0066cc',
                    textDecoration: 'none',
                    marginBottom: '1rem'
                  }}
                >
                  Visit Website <ExternalLink size={14} />
                </a>
              )}
              <Actions>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleOpenModal(partner)}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleDelete(partner.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </Actions>
            </PartnerCard>
          ))}
        </Grid>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingPartner ? 'Edit Partner' : 'Add Partner'}
      >
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Partner Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Organization name"
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Brief description of the partner..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Website URL</Label>
              <Input
                name="website_url"
                type="url"
                value={formData.website_url}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </FormGroup>

            <FormGroup>
              <Label>Logo</Label>
              <ImageUploadArea onClick={() => document.getElementById('partnerLogoInput').click()}>
                <ImageIcon size={32} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                <p>Click to upload logo</p>
                <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>JPG, PNG, GIF or SVG (Max 5MB)</p>
              </ImageUploadArea>
              <input
                id="partnerLogoInput"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoChange}
              />
              {logoPreview && (
                <PreviewImage>
                  <img src={logoPreview} alt="Preview" />
                </PreviewImage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Order</Label>
              <Input
                name="order"
                type="number"
                value={formData.order}
                onChange={handleInputChange}
                min="0"
                placeholder="Display order (lower numbers appear first)"
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (visible on landing page)
              </Label>
            </FormGroup>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                <Save size={20} style={{ marginRight: '0.5rem' }} />
                {editingPartner ? 'Update' : 'Create'} Partner
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default PartnersManagement;


